import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { byOwnerName, getOwners, NO_OWNER, storesOfOwner, type Owner } from '../../data/owners';
import { seesOwners } from '../../data/scope';
import type { Store } from '../../data/types';
import type { OwnerOf } from '../../reports/ownerColumn';

/**
 * The Owner filter (owner ruling 2026-10-28: reports and details by owner —
 * we manage some stores for third-party owners). Lives in the URL as
 * `?owner=<owner id>` (or `none`) so a filtered view can be bookmarked and
 * printed, and narrows the STORES a page works from — every report builds
 * its rows from the stores it is given, so audits, clocks and tasks follow.
 * Staff only (`seesOwners`); for store teams it is always off. The owner
 * stays picked while other filters change (and through "Clear filters") —
 * change it in its own dropdown.
 */
export interface OwnerFilter {
  /** '' = every store; NO_OWNER = stores with no owner; else an owner id. */
  owner: string;
  /** null = still loading (or not staff). */
  owners: Owner[] | null;
  /** Show the dropdown: staff, and there are owners to pick. */
  enabled: boolean;
  /** For the report's filter summary: "Owner Arcland", "No owner", ''. */
  label: string;
  apply<S extends Pick<Store, 'ownerId'>>(stores: readonly S[]): S[];
  /** The CSVs' Owner column (staff with owners loaded), else null = no column. */
  ownerOf: OwnerOf | null;
  setOwner(value: string): void;
  /** A page rebuilding its query keeps the owner in it. */
  keep(next: URLSearchParams): URLSearchParams;
}

export function useOwnerFilter(): OwnerFilter {
  const { user } = useAppUser();
  const staff = seesOwners(user?.role);
  const [params, setParams] = useSearchParams();
  const [owners, setOwners] = useState<Owner[] | null>(null);
  useEffect(() => {
    if (!staff) return;
    let live = true;
    getOwners()
      .then((list) => live && setOwners(list))
      .catch(() => live && setOwners([]));
    return () => {
      live = false;
    };
  }, [staff]);
  const owner = staff ? (params.get('owner') ?? '').trim() : '';
  return useMemo(() => {
    const named = owners?.find((o) => o.id === owner);
    const byId = new Map((owners ?? []).map((o) => [o.id, o.name]));
    const ownerOf: OwnerOf | null = staff && owners && owners.length > 0 ? (s) => (s.ownerId === null ? '' : (byId.get(String(s.ownerId)) ?? '')) : null;
    const label = !owner ? '' : owner === NO_OWNER ? 'No owner' : named ? `Owner ${named.name}` : owners ? 'Owner (not found)' : '';
    return {
      owner,
      owners,
      enabled: staff && owners !== null && (owners.length > 0 || owner !== ''),
      label,
      apply: (stores) => storesOfOwner(stores, owner),
      ownerOf,
      setOwner: (value) =>
        setParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('owner', value);
          else next.delete('owner');
          return next;
        }),
      keep: (next) => {
        if (owner && !next.has('owner')) next.set('owner', owner);
        return next;
      },
    };
  }, [owner, owners, staff, setParams]);
}

/** For a page that rebuilds its query outside the component that holds the filter: keep `?owner=`. */
export function keepOwnerParam(current: URLSearchParams, next: URLSearchParams): URLSearchParams {
  const owner = (current.get('owner') ?? '').trim();
  if (owner && !next.has('owner')) next.set('owner', owner);
  return next;
}

/** The Owner dropdown for a page's filter row (renders nothing when the filter is off). */
export function OwnerSelect({ filter }: { filter: OwnerFilter }) {
  if (!filter.enabled || filter.owners === null) return null;
  const choices = filter.owners.filter((o) => o.active || o.id === filter.owner).sort(byOwnerName);
  const unknown = filter.owner !== '' && filter.owner !== NO_OWNER && !choices.some((o) => o.id === filter.owner);
  return (
    <select value={filter.owner} onChange={(e) => filter.setOwner(e.target.value)} aria-label="Owner">
      <option value="">Owner: all</option>
      {choices.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
          {o.active ? '' : ' (retired)'}
        </option>
      ))}
      <option value={NO_OWNER}>No owner</option>
      {unknown && <option value={filter.owner}>Owner (not found)</option>}
    </select>
  );
}
